import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import { useAuth } from "@features/auth";
import type { IActiveHospital } from "../types";

import { useHospitalList } from "@features/hospitals";
import { useActiveHospital } from "../useActiveHospital";
import { registerChangeHospitalLog } from "./utils";
import { log as apiLog } from "@services/newApi/api";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import SearchIcon from "@/assets/icons/SearchIcon";
import CheckIcon from "@/assets/icons/CheckIcon";
import { queryKeys } from "@lib/queryKeys";
import { hasHospitalScope } from "@/types/role";

// =============================================================================
// Dropdown de seleção de hospital ativo (estilo DS v2 — frame 5 do dc).
// Single-select: o hospital ativo escopa os dados (backend recebe um `uuid`).
// Ancorado ao pill do topbar pelo Aside. Para role 2, trocar de hospital chama
// POST /hospitals/active — o backend re-emite o token escopado MANTENDO a
// identidade (ADR permissões-por-hospital, aposenta o switch-user). Preserva
// auto-seleção quando há 1 hospital e invalidação de reports.
// =============================================================================

const Panel = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: 340px;
  max-width: calc(100vw - 2rem);
  z-index: ${({ theme }) => theme.zIndex.modal};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.lg};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-sizing: border-box;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 48px;
  padding: 0 16px;
  border: 1.5px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radii.sm};
  box-sizing: border-box;

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.senneOrange};
    box-shadow: ${({ theme }) => theme.shadows.focusRing};
  }

  svg {
    flex: none;
    color: ${({ theme }) => theme.colors.fg3};
  }

  input {
    border: none;
    outline: none;
    background: transparent;
    width: 100%;
    font-size: ${({ theme }) => theme.typography.fs.body};
    color: ${({ theme }) => theme.colors.fg1};

    &::placeholder {
      color: ${({ theme }) => theme.colors.fg3};
    }
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 260px;
  overflow-y: auto;
`;

const Row = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 48px;
  padding: 0 12px;
  border: none;
  border-radius: ${({ theme }) => theme.radii.sm};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.tintOrange100 : "transparent"};
  color: ${({ theme }) => theme.colors.fg1};
  font-size: 16px;
  font-weight: ${({ $active }) => ($active ? 700 : 400)};
  cursor: pointer;
  text-align: left;

  &:hover {
    background: ${({ $active, theme }) =>
      $active ? theme.colors.tintOrange100 : theme.colors.gray50};
  }

  svg {
    flex: none;
    color: ${({ theme }) => theme.colors.senneOrange};
  }
`;

const Count = styled.div`
  font-size: ${({ theme }) => theme.typography.fs.sm};
  color: ${({ theme }) => theme.colors.fg3};
  text-align: center;
`;

const EmptyMsg = styled.div`
  padding: 1rem;
  text-align: center;
  font-size: ${({ theme }) => theme.typography.fs.sm};
  color: ${({ theme }) => theme.colors.fg3};
`;

const HospitalSelectDropdown = ({
  changeActiveHospitalIsOpen,
  setChangeActiveHospitalIsOpen,
  activeHospital,
  setActiveHospital,
}: {
  changeActiveHospitalIsOpen: boolean;
  setChangeActiveHospitalIsOpen: Dispatch<SetStateAction<boolean>>;
  activeHospital: IActiveHospital | undefined;
  setActiveHospital: Dispatch<SetStateAction<IActiveHospital | undefined>>;
}) => {
  const auth = useAuth();
  const { activateHospital } = useActiveHospital();
  const {
    data: hospitals,
    isLoading: isHospitalsLoading,
    error: hospitalsError,
    // Mesmo portão do backend: sem escopo hospitalar a consulta não sai. O modal só monta
    // dentro do cabeçalho já gated, e a trava repetida aqui evita que um caminho novo de
    // montagem reabra o 403 sem ninguém notar.
  } = useHospitalList(auth.user?.id ?? 0, hasHospitalScope(auth.user?.role_id));
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const hasAutoSelectedRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const isOpen =
    changeActiveHospitalIsOpen &&
    auth.user?.status !== 5 &&
    auth.user?.role_id !== 5;

  const prepareHospital = useCallback(
    (hospital: any): IActiveHospital | undefined => {
      if (!hospital?.id) return undefined;

      return {
        id: parseInt(hospital.id, 10),
        name: hospital?.name ?? "",
        uuid: hospital?.uuid ?? "",
        permissions: hospital.permissions ?? [],
        isProcedenciaActive: hospital?.isProcedenciaActive ?? false,
      };
    },
    [],
  );

  const changeActiveHospital = useCallback(
    async (uuid: string) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      try {
        if (!uuid || !hospitals) {
          apiLog.warn("hospital", "Invalid uuid or hospitals not loaded");
          return;
        }

        const hospital = hospitals.find((h) => h.uuid === uuid);
        if (!hospital) {
          console.error("No hospital found for UUID:", uuid);
          toast.error("Hospital não encontrado");
          return;
        }

        const hasHospitalChanged =
          !activeHospital?.id ||
          (hospital?.id && hospital.id !== activeHospital.id.toString());

        if (hasHospitalChanged) {
          registerChangeHospitalLog(uuid);
        }

        const newActiveHospital = prepareHospital(hospital);
        setActiveHospital(newActiveHospital);

        if (auth.user?.role_id === 2) {
          try {
            // Escopo passa pelo contexto (POST /hospitals/active + revalidação):
            // é ele que mantém o `scopeReady`, e uma segunda cópia desta chamada
            // deixaria a flag mentindo depois de trocar de hospital.
            await activateHospital(Number(hospital.id));
          } catch (error) {
            console.error("Erro ao definir hospital ativo:", (error as any)?.message);
            toast.error("Erro ao trocar de hospital. Tente novamente.");
            setActiveHospital(undefined);
            return;
          }
        }

        try {
          await queryClient.invalidateQueries({ queryKey: queryKeys.reports.all() });
        } catch (error) {
          console.error("Error invalidating reports query:", (error as any)?.message);
        }

        setSearch("");
        setChangeActiveHospitalIsOpen(false);
      } catch (error) {
        console.error("Error changing active hospital:", (error as any)?.message);
        toast.error("Erro ao alterar hospital");
        setActiveHospital(undefined);
      }
    },
    [
      hospitals,
      activeHospital?.id,
      auth,
      activateHospital,
      prepareHospital,
      setActiveHospital,
      setChangeActiveHospitalIsOpen,
      queryClient,
    ],
  );

  // Auto-seleção quando o usuário tem exatamente um hospital.
  useEffect(() => {
    if (
      hospitals?.length === 1 &&
      !hasAutoSelectedRef.current &&
      changeActiveHospitalIsOpen &&
      !activeHospital?.id
    ) {
      hasAutoSelectedRef.current = true;
      changeActiveHospital(hospitals[0]!.uuid);
    }
  }, [hospitals, changeActiveHospitalIsOpen, changeActiveHospital, activeHospital?.id]);

  useEffect(() => {
    if (!changeActiveHospitalIsOpen) {
      hasAutoSelectedRef.current = false;
      setSearch("");
    }
  }, [changeActiveHospitalIsOpen]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (hospitalsError) {
      console.error("Error loading hospitals:", hospitalsError);
      toast.error("Erro ao carregar hospitais");
    }
  }, [hospitalsError]);

  const filtered = useMemo(() => {
    const list = hospitals ?? [];
    const term = search.trim().toLowerCase();
    if (!term) return list;
    return list.filter((h) => (h.name ?? "").toLowerCase().includes(term));
  }, [hospitals, search]);

  if (!isOpen) return null;

  return (
    <Panel role="dialog" aria-label="Selecionar hospital ativo">
      <SearchBox>
        <SearchIcon />
        <input
          type="text"
          autoFocus
          placeholder="Buscar hospital…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Buscar hospital"
        />
      </SearchBox>

      {isHospitalsLoading ? (
        <EmptyMsg>Carregando hospitais…</EmptyMsg>
      ) : filtered.length === 0 ? (
        <EmptyMsg>Nenhum hospital encontrado.</EmptyMsg>
      ) : (
        <List>
          {filtered.map((hospital) => {
            const active = hospital.uuid === activeHospital?.uuid;
            return (
              <Row
                key={hospital.uuid}
                type="button"
                $active={active}
                onClick={() => changeActiveHospital(hospital.uuid)}
              >
                <span>{hospital.name}</span>
                {active && <CheckIcon />}
              </Row>
            );
          })}
        </List>
      )}

      <Count>
        {(hospitals?.length ?? 0)}{" "}
        {(hospitals?.length ?? 0) === 1 ? "hospital" : "hospitais"} no total
      </Count>
    </Panel>
  );
};

export default HospitalSelectDropdown;
